'use client'

import {
    Modal as BaseComponent,
    type ModalContentProps as BaseModalContentProps,
    type ModalProps as BaseProps,
    type CloseButtonProps,
    ModalBody,
    type ModalBodyProps,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    type ModalFooterProps,
    ModalHeader,
    type ModalHeaderProps,
    ModalOverlay,
    type ModalOverlayProps,
    useModalContext,
    useModalStyles,
} from '@chakra-ui/react'
import { type JSX, type ReactNode, useEffect, useState } from 'react'
import { CloseIcon } from '../../icons'
import type { ButtonProps } from '../Button'
import { Button } from '../Button'
import { Icon } from '../Icon/Icon'
import { HStack } from '../Layout'
import { Heading, Text } from '../Typography'

type Omitted =
    | 'colorScheme'
    | 'variant'
    | 'getContainer'
    | 'lockFocusAcrossFrames'
    | 'isCentered'
    | 'scrollBehavior'
    | 'motionPreset'
    | 'closeOnOverlayClick'
    | 'onOverlayClick'
    | 'closeOnEsc'
    | 'onEsc'
    | 'useInert'
    | 'trapFocus'
    | 'autoFocus'
    | 'returnFocusOnClose'
    | 'blockScrollOnMount'
    | 'allowPinchZoom'
    | 'preserveScrollBarGap'
    | 'overlay'

export type ModalProps = Omit<BaseProps, Omitted> & {
    /** @default 'sm' */
    size?: 'sm' | 'md' | 'lg'
    /** @default true */
    overlay?: boolean
    /** @default false */
    isBlurOverlay?: boolean
}

type CustomModalHeaderProps = ModalHeaderProps & {
    hint?: string
}

const Overlay = (props: ModalOverlayProps) => <ModalOverlay {...props} />
const Header = (props: CustomModalHeaderProps) => (
    <ModalHeader {...props}>
        {props.hint && <Text color="neutral.1600">{props.hint}</Text>}
        <Heading size="h6" fontWeight="semibold">
            {props.children}
        </Heading>
    </ModalHeader>
)

const CloseButton = ({
    isFloating,
    ...props
}: CloseButtonProps & { isFloating?: boolean }) => (
    <ModalCloseButton
        position={isFloating ? 'absolute' : 'static'}
        {...props}
    />
)

const MobileCloseBar = ({ text, ...props }) => {
    const styles = useModalStyles()
    const { onClose } = useModalContext()

    return (
        <HStack sx={styles.closeBar} onClick={onClose} {...props}>
            <Icon size={'md'} as={CloseIcon} />
            <Text color={'neutral.1900'}>{text}</Text>
        </HStack>
    )
}

type ModalCloseProps = {
    text: string
    isFloating?: boolean
}

const Close = ({ text, isFloating = true }: ModalCloseProps) => (
    <>
        <CloseButton isFloating={isFloating} />
        <MobileCloseBar text={text} />
    </>
)

type ModalContentProps = BaseModalContentProps & {
    children: ReactNode
}

const Content = ({ children, ...props }: ModalContentProps) => (
    <ModalContent {...props}>{children}</ModalContent>
)

const Body = (props: ModalBodyProps) => {
    const [scrolling, setScrolling] = useState(false)

    useEffect(() => {
        let scrollTimeout: NodeJS.Timeout

        if (scrolling) {
            scrollTimeout = setTimeout(() => setScrolling(false), 500)
        }

        return () => {
            clearTimeout(scrollTimeout)
        }
    }, [scrolling])

    return (
        <ModalBody
            data-scroll={scrolling}
            onScroll={() => setScrolling(true)}
            {...props}
        />
    )
}

const Footer: React.FC<ModalFooterProps> = ModalFooter

const ModalFooterButton = (props: ButtonProps) => {
    return <Button width={['full', 'unset']} {...props} />
}

const DefaultModal = ({
    children,
    size = 'sm',
    overlay = true,
    ...props
}: ModalProps) => (
    <BaseComponent
        scrollBehavior="inside"
        motionPreset={size === 'lg' ? 'slideInBottom' : 'scale'}
        size={['full', size]}
        {...props}
    >
        {overlay && <Overlay />}
        {children}
    </BaseComponent>
)

export const Modal = (
    props: React.PropsWithChildren<ModalProps>,
): JSX.Element => (props.isOpen ? <DefaultModal {...props} /> : <></>)

Modal.Header = Header
Modal.Content = Content
Modal.Close = Close
Modal.Body = Body
Modal.Footer = Footer
Modal.CloseButton = CloseButton
Modal.FooterButton = ModalFooterButton

export type { Omitted as ModalOmittedKeys }
